import * as React from 'react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useBusinessPlanOptions } from 'constants/businessPlan';
import { useBreakpoints } from 'hooks';

import { useAIActionHandler } from 'pages/BusinessToolPage/components/BusinessToolAiProvider/hooks/useAIActionHandler';

import { IBusinessPlanType } from 'interfaces/businessPlan';

import { getDocumentType } from 'helpers/getDocumentType';

import { SelectorContentView } from './components/SelectorContentView';
import { SelectorMenuToggleButton } from './components/SelectorMenuToggleButton';
import { SelectTemplateItem } from './components/SelectTemplateItem';
import { ISelectTemplateProps } from './types';
import { Button } from '../Button/Button';

export const SelectTemplate: FC<ISelectTemplateProps> = ({ template, isLoading, onTemplateChange }) => {
  const { handleActionWithAICheck } = useAIActionHandler();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [templateToMobile, setTemplateToMobile] = React.useState<IBusinessPlanType>();

  const { isMobile } = useBreakpoints();
  const { t } = useTranslation();
  const businessPlanOptions = useBusinessPlanOptions();

  const handleClose = () => setAnchorEl(null);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);

  const isChecked = (value: IBusinessPlanType) => getDocumentType(template) === getDocumentType(value);
  const isMobileChecked = (value: IBusinessPlanType) =>
    templateToMobile
      ? getDocumentType(templateToMobile) === getDocumentType(value)
      : getDocumentType(template) === getDocumentType(value);

  const getCheckedItem = () => businessPlanOptions.find(({ template }) => isChecked(template));

  const selectedOption = businessPlanOptions.find(
    (option) => getDocumentType(option.template) === getDocumentType(template),
  );

  const handleTemplateChange = (newTemplate: IBusinessPlanType) => {
    if (isMobile) {
      setTemplateToMobile(newTemplate);
      return;
    }
    handleActionWithAICheck(() => onTemplateChange(newTemplate));
  };

  const handleMobileTemplateChange = (newTemplate: IBusinessPlanType) => {
    handleActionWithAICheck(() => onTemplateChange(newTemplate));
  };

  const cssVariables = {
    '--plan-color': selectedOption?.color,
    '--plan-background': selectedOption?.backgroundColor,
    '--plan-borderColor': selectedOption?.borderColor,
  } as React.CSSProperties;

  return (
    <Box style={cssVariables}>
      <SelectorMenuToggleButton
        onClick={handleClick}
        icon={getCheckedItem()?.icon}
        label={getCheckedItem()?.label}
        isLoading={isLoading}
      />
      <SelectorContentView
        anchorEl={anchorEl}
        isOpened={Boolean(anchorEl)}
        onClose={handleClose}
        title={t('selectTemplate')}
      >
        {businessPlanOptions.map((item) => (
          <SelectTemplateItem
            key={item.template}
            {...item}
            close={handleClose}
            isSelected={!isMobile ? isChecked(item.template) : isMobileChecked(item.template)}
            onTemplateChange={handleTemplateChange}
          />
        ))}
        {isMobile && (
          <Button
            variant="contained"
            fullWidth
            sx={{ marginTop: '24px' }}
            onClick={() => {
              templateToMobile && handleMobileTemplateChange(templateToMobile);
            }}
            isLoading={isLoading}
          >
            Apply
          </Button>
        )}
      </SelectorContentView>
    </Box>
  );
};
